import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  AlertTriangle, Bell, Info, Loader2, Send, User as UserIcon,
} from 'lucide-react';
import {
  useCreateNotification, useNotificationRecipients,
} from '../hooks/api/notifications';

const SEVERITIES = [
  { value: 'critical', label: 'Critical', icon: AlertTriangle, color: 'text-red-400' },
  { value: 'warning',  label: 'Warning',  icon: Bell,          color: 'text-amber-400' },
  { value: 'info',     label: 'Info',     icon: Info,          color: 'text-sky-400' },
];

const BROADCAST = '__broadcast__'; // non-empty placeholder so the Select keeps a value

export function CreateAlertDialog({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState('info');
  const [recipientId, setRecipientId] = useState(BROADCAST);
  const [error, setError] = useState('');

  const recipients = useNotificationRecipients({ enabled: open });
  const create = useCreateNotification();

  // Reset every time the dialog re-opens so old form state doesn't leak.
  useEffect(() => {
    if (open) {
      setTitle(''); setBody('');
      setSeverity('info'); setRecipientId(BROADCAST);
      setError('');
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    try {
      const payload = {
        title: title.trim(),
        body: body.trim() || undefined,
        type: severity,
        user_id: recipientId === BROADCAST ? null : recipientId,
      };
      const created = await create.mutateAsync(payload);
      onCreated?.(created);
      onOpenChange(false);
    } catch (err) {
      const first = err?.details?.errors?.[0];
      const path = first?.loc?.slice(1).join('.');
      setError(path ? `${path}: ${first.msg}` : err?.message || 'Could not create alert');
    }
  };

  const submitting = create.isPending;
  const recipientList = recipients.data || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-sky-400" /> Create Alert
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Severity row — clickable chip-style to match Alerts page tone */}
          <div className="space-y-2">
            <Label>Severity</Label>
            <div className="grid grid-cols-3 gap-2">
              {SEVERITIES.map((s) => {
                const active = severity === s.value;
                const Icon = s.icon;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSeverity(s.value)}
                    className={
                      'rounded-lg border p-3 flex flex-col items-center gap-1 transition ' +
                      (active
                        ? 'bg-white/10 border-sky-500/50'
                        : 'bg-card border-border hover:bg-white/5')
                    }
                  >
                    <Icon className={`w-4 h-4 ${s.color}`} />
                    <span className="text-xs">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="alert-title">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="alert-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. New encroachment detected at site B"
              maxLength={255}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="alert-body">Message</Label>
            <Textarea
              id="alert-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Optional details about what happened, severity, recommended action…"
              rows={4}
              maxLength={2048}
              className="resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label>Assign to</Label>
            <Select value={recipientId} onValueChange={setRecipientId} disabled={recipients.isLoading}>
              <SelectTrigger>
                <SelectValue placeholder={recipients.isLoading ? 'Loading members…' : 'Pick a recipient'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BROADCAST}>
                  <span className="inline-flex items-center gap-2">
                    <UserIcon className="w-3.5 h-3.5" />
                    All client members (broadcast)
                  </span>
                </SelectItem>
                {recipientList.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    <div className="flex items-center justify-between gap-3 w-full">
                      <span className="truncate">{r.full_name}</span>
                      <span className="text-[10px] text-muted-foreground font-mono">{r.email}</span>
                      {r.is_client_admin && (
                        <Badge variant="outline" className="text-[10px]">Admin</Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Pick a single user to send a direct alert, or leave on "All client members" to broadcast.
              The alert appears in their Alerts tab and the bell icon at the top-right immediately.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !title.trim()}>
              {submitting
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Send className="w-4 h-4 mr-2" />}
              Send Alert
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
